'use client';

import { useState } from 'react';
import type { UktData, IncomeOption } from '@/lib/db-admission';

function formatRupiah(n: number): string {
  return 'Rp ' + n.toLocaleString('id-ID');
}

function estimateTier(uni: UktData, maxIncome: number): UktData['tiers'][number] {
  const thresholds = uni.incomeThresholds;
  for (let i = 0; i < thresholds.length; i++) {
    if (maxIncome <= thresholds[i]) {
      return uni.tiers[i] || uni.tiers[uni.tiers.length - 1];
    }
  }
  return uni.tiers[uni.tiers.length - 1];
}

interface UktEstimatorProps {
  /** Pre-select a university */
  universityId?: string;
  lang: string;
  universities: UktData[];
  incomeOptions: IncomeOption[];
}

export function UktEstimator({ universityId, lang, universities, incomeOptions }: UktEstimatorProps) {
  const isId = lang === 'id';
  const uniIds = universities.map(u => u.universityId);
  const [selectedUni, setSelectedUni] = useState<string>(
    (universityId && uniIds.includes(universityId)) ? universityId : uniIds[0]
  );
  const [selectedIncome, setSelectedIncome] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');

  const uni = universities.find(u => u.universityId === selectedUni);
  if (!uni) return null;

  const programs = uni.programs;
  const program = selectedProgram && programs ? programs[selectedProgram] : null;
  const incomeOption = incomeOptions.find(o => o.id === selectedIncome);
  const baseTier = incomeOption ? estimateTier(uni, incomeOption.maxIncome) : null;
  const multiplier = program?.tiersMultiplier ?? 1;
  const estimatedTier = baseTier ? {
    ...baseTier,
    semester: Math.round(baseTier.semester * multiplier),
    monthly: Math.round(baseTier.monthly * multiplier),
  } : null;

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-card p-4 md:p-6">
      <h3 className="font-heading text-lg font-bold text-text-primary">
        {isId ? 'Estimasi Biaya UKT' : 'UKT Tuition Estimator'}
      </h3>
      <p className="mt-1 text-sm text-text-secondary">
        {isId
          ? 'Pilih universitas dan penghasilan orang tua untuk melihat estimasi UKT per semester.'
          : 'Select university and parents\' income to see estimated tuition per semester.'}
      </p>

      <div className={`mt-4 grid gap-4 ${programs ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        {/* University selector */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-text-secondary uppercase tracking-wider">
            {isId ? 'Universitas' : 'University'}
          </label>
          <select
            value={selectedUni}
            onChange={e => { setSelectedUni(e.target.value); setSelectedProgram(''); }}
            className="w-full rounded-lg border border-border-subtle bg-surface-root px-3 py-2 text-sm text-text-primary"
          >
            {universities.map(u => (
              <option key={u.universityId} value={u.universityId}>{u.abbrev} — {u.name}</option>
            ))}
          </select>
        </div>

        {/* Income selector */}
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-text-secondary uppercase tracking-wider">
            {isId ? 'Penghasilan Orang Tua (per bulan)' : 'Parents\' Monthly Income'}
          </label>
          <select
            value={selectedIncome}
            onChange={e => setSelectedIncome(e.target.value)}
            className="w-full rounded-lg border border-border-subtle bg-surface-root px-3 py-2 text-sm text-text-primary"
          >
            <option value="">{isId ? '— Pilih penghasilan —' : '— Select income —'}</option>
            {incomeOptions.map(opt => (
              <option key={opt.id} value={opt.id}>{isId ? opt.labelId : opt.labelEn}</option>
            ))}
          </select>
        </div>

        {/* Program selector (only for universities with program data) */}
        {programs && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-text-secondary uppercase tracking-wider">
              {isId ? 'Program Studi' : 'Study Program'}
            </label>
            <select
              value={selectedProgram}
              onChange={e => setSelectedProgram(e.target.value)}
              className="w-full rounded-lg border border-border-subtle bg-surface-root px-3 py-2 text-sm text-text-primary"
            >
              <option value="">{isId ? '— Umum (semua prodi) —' : '— General (all programs) —'}</option>
              {Object.entries(programs).map(([key, prog]) => (
                <option key={key} value={key}>{isId ? prog.nameId : prog.nameEn}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Result */}
      {estimatedTier && (
        <div className="mt-4 rounded-lg panel-success p-4">
          <p className="text-sm font-semibold text-semantic-success">
            {isId ? 'Estimasi UKT Kamu' : 'Your Estimated UKT'}
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            <div>
              <p className="text-xs text-text-secondary">{isId ? 'Golongan' : 'Tier'}</p>
              <p className="text-lg font-bold text-text-primary">{isId ? 'Golongan' : 'Group'} {estimatedTier.group}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary">{isId ? 'Per Semester' : 'Per Semester'}</p>
              <p className="text-lg font-bold text-text-primary">{formatRupiah(estimatedTier.semester)}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary">{isId ? 'Per Bulan (estimasi)' : 'Per Month (est.)'}</p>
              <p className="text-lg font-bold text-text-primary">~{formatRupiah(estimatedTier.monthly)}</p>
            </div>
          </div>
          <p className="mt-2 text-xs text-text-tertiary">
            {isId
              ? `* Estimasi berdasarkan data UKT ${uni.abbrev} ${uni.year}${program ? ` (${program.nameId}, ×${multiplier})` : ''}. Golongan UKT final ditentukan oleh universitas.`
              : `* Estimated based on ${uni.abbrev} ${uni.year} UKT data${program ? ` (${program.nameEn}, ×${multiplier})` : ''}. Final tier is determined by the university.`}
          </p>
        </div>
      )}

      {/* Full tier table */}
      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-medium text-accent-primary hover:underline">
          {isId ? `Lihat semua golongan UKT ${uni.abbrev}` : `View all ${uni.abbrev} UKT tiers`}
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border-subtle text-xs text-text-secondary">
                <th className="pb-2 pr-3 font-medium">{isId ? 'Golongan' : 'Group'}</th>
                <th className="pb-2 pr-3 font-medium">{isId ? 'UKT/Semester' : 'UKT/Semester'}</th>
                <th className="pb-2 font-medium">{isId ? '~Per Bulan' : '~Per Month'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {uni.tiers.map(tier => (
                <tr
                  key={tier.group}
                  className={estimatedTier?.group === tier.group ? 'panel-success' : ''}
                >
                  <td className="py-2 pr-3 font-medium text-text-primary">
                    {tier.group}
                    {estimatedTier?.group === tier.group && (
                      <span className="ml-1.5 text-xs text-semantic-success">&larr; {isId ? 'kamu' : 'you'}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-text-primary">{formatRupiah(Math.round(tier.semester * multiplier))}</td>
                  <td className="py-2 text-text-secondary">~{formatRupiah(Math.round(tier.monthly * multiplier))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-text-tertiary">
          {isId ? 'Sumber: ' : 'Source: '}
          <a href={uni.source} target="_blank" rel="noopener noreferrer" className="underline hover:text-text-primary">
            {uni.source}
          </a>
        </p>
      </details>
    </div>
  );
}
